import { useBrandRegionCode } from "core/hooks/useBrandRegionCode";
import { useCentrixApi } from "core/hooks/useCentrixApi";
import { useCentrixFetch } from "core/hooks/useCentrixFetch";
import { useLocale } from "core/hooks/useLocale";
import { buildQueryString } from "core/utils/buildQueryString";
import { getBrandCode } from "core/utils/getBrandCode";
import { useCallback, useMemo } from "react";
import type { Notification } from "./NotificationDefs";

type NotificationEndpointResponseJson = {
  totalCount: number;
  totalUnseenCount: number;
  totalUnreadCount: number;
  items: Notification[];
};

// milliseconds * seconds * minutes
const ONE_MINUTE_IN_MILLISECONDS = 1000 * 60 * 1;

export function useNotifications() {
  const locale = useLocale();
  const brandCode = getBrandCode();
  const regionCode = useBrandRegionCode();
  const {
    data: apiData,
    isError,
    isLoading,
    mutate,
  } = useCentrixApi({
    path: "/api/app/notifications",
    parameters: { query: { Locale: locale } },
    swrOptions: {
      refreshInterval: ONE_MINUTE_IN_MILLISECONDS,
      revalidateIfStale: false,
    },
  });
  const data = apiData as NotificationEndpointResponseJson;
  const centrixFetch = useCentrixFetch();

  const notifications = useMemo(() => {
    if (!data || !Array.isArray(data.items)) {
      return [];
    }
    return data.items.map((item) => {
      if (item.itemType !== "DmsDocument") {
        return item;
      }
      if (!("dynamicData" in item)) {
        return item;
      }
      if (item?.dynamicData?.type !== "file") {
        return item;
      }
      const { filename, fileId } = item.dynamicData;
      const queryString = buildQueryString({
        fileId,
        brandCode,
        regionCode,
        locale,
      });
      const uri = `/api/dms-document/${encodeURIComponent(
        filename
      )}?${queryString}`;
      return { ...item, uri };
    });
  }, [data, brandCode, regionCode, locale]);

  const unreadNotificationParentIds = useMemo(() => {
    const ids = notifications.reduce(
      (acc: Set<string>, currentNotification) => {
        if (currentNotification.isRead) {
          return acc;
        }
        if (!("dynamicData" in currentNotification)) {
          return acc;
        }
        if (
          !currentNotification.dynamicData ||
          typeof currentNotification.dynamicData !== "object"
        ) {
          return acc;
        }
        if (!("parents" in currentNotification.dynamicData)) {
          return acc;
        }
        const { parents } = currentNotification.dynamicData;
        if (!Array.isArray(parents)) {
          return acc;
        }
        parents.forEach((id: string) => {
          acc.add(id);
        });
        return acc;
      },
      new Set<string>()
    );
    return ids;
  }, [notifications]);

  const unreadNotificationItemIds = useMemo(() => {
    const itemIds = notifications.reduce((acc, currentNotification) => {
      if (currentNotification.isRead) {
        return acc;
      }
      acc.add(currentNotification.itemId);
      return acc;
    }, new Set<string>());
    return itemIds;
  }, [notifications]);

  const shouldEntityHaveNotificationDotById = useCallback(
    (id: string) =>
      unreadNotificationParentIds.has(id) || unreadNotificationItemIds.has(id),
    [unreadNotificationItemIds, unreadNotificationParentIds]
  );

  const hasUnreadNotification = useCallback(
    (id: string) => unreadNotificationItemIds.has(id),
    [unreadNotificationItemIds]
  );

  const hasUnreadNotificationChildren = useCallback(
    (id: string) => unreadNotificationParentIds.has(id),
    [unreadNotificationParentIds]
  );

  const totalUnseenCount = data?.totalUnseenCount ?? 0;
  const totalUnreadCount = data?.totalUnreadCount ?? 0;

  const patchNotificationIsRead = useCallback(
    async (id: string, isRead: boolean) => {
      await mutate(
        async () => {
          const response = await centrixFetch({
            method: "post",
            path: `/api/app/notifications/{id}/mark-read-unread`,
            parameters: { path: { id } },
            body: {
              isRead,
              isSeen: true,
            },
          });
          if (!response.ok) {
            throw new Error(response.statusText);
          }
          return {} as any; // data is manually set in the optimisticData function. This is just to satisfy the types.
        },
        {
          optimisticData: (currentData) => {
            if (!currentData || !Array.isArray(currentData.items)) {
              return {
                items: [],
                totalCount: 0,
                totalUnseenCount: 0,
                totalUnreadCount: 0,
              };
            }
            const updatedItems = currentData.items.map((item) => {
              if (item.id !== id) {
                return item;
              }
              return { ...item, isRead };
            });
            const newTotalUnreadCount = isRead
              ? (currentData.totalUnreadCount ?? 0) - 1
              : (currentData.totalUnreadCount ?? 0) + 1;
            const newTotalUnseenCount =
              (currentData.totalUnseenCount ?? 0) > 0
                ? (currentData.totalUnseenCount ?? 0) - 1
                : 0;
            const newNotificationsData: NotificationEndpointResponseJson = {
              ...currentData,
              totalCount: currentData.totalCount ?? 0,
              items: updatedItems as any,
              totalUnreadCount: newTotalUnreadCount,
              totalUnseenCount: newTotalUnseenCount,
            };
            return newNotificationsData as any;
          },
          populateCache: false,
          rollbackOnError: true,
        }
      );
    },
    [mutate, centrixFetch]
  );

  const markNotificationAsRead = useCallback(
    (id: string) => patchNotificationIsRead(id, true),
    [patchNotificationIsRead]
  );

  const markNotificationAsReadByItemId = useCallback(
    (itemId: string) => {
      const notification = notifications
        .filter(({ isRead }) => !isRead)
        .find((currentNotification) => currentNotification.itemId === itemId);
      if (!notification) {
        return Promise.resolve();
      }
      return markNotificationAsRead(notification.id);
    },
    [notifications, markNotificationAsRead]
  );

  const markNotificationAsUnread = useCallback(
    (id: string) => patchNotificationIsRead(id, false),
    [patchNotificationIsRead]
  );

  const markAllNotificationsAsSeen = useCallback(async () => {
    await mutate(
      async () => {
        const response = await centrixFetch({
          method: "post",
          path: "/api/app/notifications/mark-all-seen",
        });
        if (!response.ok) {
          throw new Error(response.statusText);
        }
        return {} as any; //  data is manually set in the optimisticData function. This is just to satisfy the types.
      },
      {
        optimisticData: (currentData) => {
          if (!currentData || !Array.isArray(currentData?.items)) {
            return {
              items: [],
              totalCount: 0,
              totalUnseenCount: 0,
              totalUnreadCount: 0,
            };
          }
          const updatedItems = currentData.items.map((item) => ({
            ...item,
            isSeen: true,
          }));
          const newData = {
            ...currentData,
            items: updatedItems,
            totalUnseenCount: 0,
          };
          return newData;
        },
        populateCache: false,
        revalidate: false,
        rollbackOnError: true,
      }
    );
  }, [centrixFetch, mutate]);

  const markAllNotificationsAsRead = useCallback(async () => {
    await mutate(
      async () => {
        const response = await centrixFetch({
          method: "post",
          path: "/api/app/notifications/mark-all-read",
        });
        if (!response.ok) {
          throw new Error(response.statusText);
        }
        return {} as any;
      },
      {
        optimisticData: (currentData) => {
          if (!currentData || !Array.isArray(currentData.items)) {
            return {
              items: [],
              totalCount: 0,
              totalUnseenCount: 0,
              totalUnreadCount: 0,
            };
          }
          const updatedItems = currentData.items.map((item) => ({
            ...item,
            isRead: true,
            isSeen: true,
          }));
          return {
            ...currentData,
            items: updatedItems,
            totalUnseenCount: 0,
            totalUnreadCount: 0,
          };
        },
        populateCache: false,
        rollbackOnError: true,
      }
    );
  }, [centrixFetch, mutate]);

  const deleteNotification = useCallback(
    async (id: string) => {
      await mutate(
        async () => {
          const response = await centrixFetch({
            method: "delete",
            path: `/api/app/notifications/{id}`,
            parameters: { path: { id } },
          });
          if (!response.ok) {
            throw new Error(response.statusText);
          }
          return {} as any; // data is manually set in the optimisticData function. This is just to satisfy the types.
        },
        {
          optimisticData: (currentData) => {
            if (!currentData || !Array.isArray(currentData.items)) {
              return {
                items: [],
                totalCount: 0,
                totalUnseenCount: 0,
                totalUnreadCount: 0,
              };
            }
            let decrementTotalUnreadCount = false;
            let decrementTotalUnseenCount = false;
            const updatedItems = currentData.items.filter((item) => {
              const shouldKeep = item.id !== id;
              if (!shouldKeep) {
                if (!item.isRead) {
                  decrementTotalUnreadCount = true;
                }
                if (!item.isSeen) {
                  decrementTotalUnseenCount = true;
                }
              }
              return shouldKeep;
            });
            return {
              ...currentData,
              items: updatedItems,
              totalUnseenCount: decrementTotalUnseenCount
                ? (currentData.totalUnseenCount ?? 0) - 1
                : currentData.totalUnseenCount,
              totalUnreadCount: decrementTotalUnreadCount
                ? (currentData.totalUnreadCount ?? 0) - 1
                : currentData.totalUnreadCount,
            };
          },
          populateCache: false,
          rollbackOnError: true,
        }
      );
    },
    [centrixFetch, mutate]
  );

  const deleteAllNotifications = useCallback(async () => {
    await mutate(
      async () => {
        const response = await centrixFetch({
          method: "delete",
          path: `/api/app/notifications`,
        });
        if (!response.ok) {
          throw new Error(response.statusText);
        }
        return {} as any; // data is manually set in the optimisticData function. This is just to satisfy the types.
      },
      {
        optimisticData: (currentData) => {
          if (!currentData) {
            return {
              items: [],
              totalCount: 0,
              totalUnseenCount: 0,
              totalUnreadCount: 0,
            };
          }
          return {
            ...currentData,
            items: [],
            totalUnseenCount: 0,
            totalUnreadCount: 0,
          };
        },
        populateCache: false,
        rollbackOnError: true,
      }
    );
  }, [centrixFetch, mutate]);

  return {
    data,
    isError,
    isLoading,
    mutate: mutate as any,
    notifications,
    totalCount: data?.totalCount ?? 0,
    totalUnreadCount,
    totalUnseenCount,
    markNotificationAsRead,
    markNotificationAsUnread,
    markAllNotificationsAsRead,
    markAllNotificationsAsSeen,
    markNotificationAsReadByItemId,
    deleteNotification,
    deleteAllNotifications,
    shouldEntityHaveNotificationDotById,
    hasUnreadNotification,
    hasUnreadNotificationChildren,
  };
}
